import React, { useMemo } from 'react';
import PropTypes, { func } from 'prop-types';
import {
  Body1Stronger,
  Combobox,
  makeStyles,
  mergeClasses,
  Option,
  tokens,
} from '@fluentui/react-components';

import { fontWeights } from '../constants';

const useFontsSelectorStyles = makeStyles({
  fontWeightSelectWrapper: {
    position: 'relative',
  },
  fontWeightSelect: {
    minWidth: '160px',
    width: '100%',
    marginTop: tokens.spacingVerticalS,
    '& > input::selection': {
      backgroundColor: 'transparent',
    },
  },
});

export const FontsWeightSelector = ({
  id,
  i18n,
  label,
  value,
  onChange,
  className,
  inlinePopup,
  placeholder,
  selectClassName,
  overrideFontWeights,
}) => {
  const classes = useFontsSelectorStyles();
  const fontWeightSelectId = `${label}-font-weight-select`;

  const fontWeightOptions = overrideFontWeights || fontWeights;

  const selectedFontWeightText = useMemo(() => {
    const selectedFontWeight =
      fontWeightOptions.find(weight => weight.key === value) || null;
    return selectedFontWeight ? selectedFontWeight.text : '';
  }, [value, fontWeightOptions]);

  const handleWeightChange = (_ev, data) => {
    if (data && data.optionValue) {
      onChange(data.optionValue);
    }
  };

  return (
    <div className={mergeClasses(`${id}-font-weight-selector`, 'ads-fonts-weight-selector', className)}>
      {label && <Body1Stronger>{label}</Body1Stronger>}
      <div className={classes.fontWeightSelectWrapper}>
        <Combobox
          freeform={false}
          id={fontWeightSelectId}
          inlinePopup={inlinePopup}
          selectedOptions={[value]}
          onOptionSelect={handleWeightChange}
          placeholder={placeholder || i18n.getString(_TL_('Select weight'))}
          value={
            selectedFontWeightText
              ? i18n.getString(selectedFontWeightText)
              : null
          }
          className={mergeClasses(
            `${id}-font-weight-select`,
            'ads-fonts-weight-select',
            classes.fontWeightSelect,
            selectClassName
          )}
        >
          {fontWeightOptions.map(weight => (
            <Option
              id={weight.key}
              key={weight.key}
              text={weight.key}
              value={weight.key}
              aria-label={i18n.getString(weight.text)}
              className="ads-fonts-weight-select-option"
            >
              {i18n.getString(weight.text)}
            </Option>
          ))}
        </Combobox>
      </div>
    </div>
  );
};

FontsWeightSelector.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  overrideFontWeights: PropTypes.arrayOf({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }),
  className: PropTypes.string,
  selectClassName: PropTypes.string,
  inlinePopup: PropTypes.bool,
  placeholder: PropTypes.string,
};

FontsWeightSelector.defaultProps = {
  label: '',
  className: '',
  placeholder: null,
  selectClassName: '',
  inlinePopup: false,
  overrideFontWeights: null,
};
